import { ReactNode } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { SURFACE } from "../../common/designTokens";
import { WARN_TILE } from "./constants";

export interface TileProps {
  label: ReactNode;
  value: ReactNode;
  unit?: string;
  sub?: ReactNode;
  foot?: ReactNode;
  /** The lapse tile: amber surface, dark-amber ink. */
  warn?: boolean;
  /** No data yet (e.g. CN before phase 2): the value is drawn muted. */
  muted?: boolean;
  /** A tile that filters the list below: drawn as a toggle button. */
  onClick?: () => void;
  selected?: boolean;
}

/** One KPI tile of the GM dashboard (design `Dashboard GM`). */
export default function Tile({ label, value, unit, sub, foot, warn, muted, onClick, selected }: Readonly<TileProps>) {
  const ink2 = warn ? WARN_TILE.ink : SURFACE.tx2;
  const border = selected ? SURFACE.tx : warn ? WARN_TILE.border : SURFACE.line;
  const body = (
    <>
      <Typography component="span" sx={{ display: "block", fontSize: "0.8125rem", color: ink2 }}>
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          display: "block",
          fontSize: "1.625rem",
          fontWeight: 600,
          lineHeight: 1.35,
          fontVariantNumeric: "tabular-nums",
          color: muted ? SURFACE.tx3 : SURFACE.tx,
        }}
      >
        {value}
        {unit && (
          <Box component="span" sx={{ fontSize: "0.875rem", fontWeight: 400, ml: 0.5 }}>
            {unit}
          </Box>
        )}
      </Typography>
      {sub && (
        <Typography component="span" sx={{ display: "block", fontSize: "0.8125rem", color: ink2 }}>
          {sub}
        </Typography>
      )}
      {foot && (
        <Typography component="span" sx={{ display: "block", fontSize: "0.8125rem", mt: 0.5, color: SURFACE.tx }}>
          {foot}
        </Typography>
      )}
    </>
  );
  const sx = {
    bgcolor: warn ? WARN_TILE.bg : SURFACE.card,
    border: `1px solid ${border}`,
    boxShadow: selected ? `inset 0 0 0 1px ${SURFACE.tx}` : "none",
    borderRadius: "10px",
    p: 1.75,
    minWidth: 0,
  };
  if (!onClick) return <Box sx={sx}>{body}</Box>;
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={Boolean(selected)}
      sx={{ ...sx, display: "block", textAlign: "left", width: "100%", "&:hover": { borderColor: SURFACE.tx2 } }}
    >
      {body}
    </ButtonBase>
  );
}

export function TileGrid({ columns, children }: Readonly<{ columns: number; children: ReactNode }>) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: `repeat(${columns}, minmax(0, 1fr))`,
        },
        gap: 1.25,
      }}
    >
      {children}
    </Box>
  );
}
